import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  IconButton,
  InputBase,
  Divider,
  Menu,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { formatPersonName } from "../../../utils/formatPersonName";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PixIcon from "@mui/icons-material/Pix";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { getTransactionsReport } from "./reportApi";
import { buildAdminWhatsAppUrl, openAdminWhatsApp } from "../../../utils/adminWhatsApp";

const PERIOD_PRESETS = {
  today: "Hoje",
  month: "Este mês",
  year: "Este ano",
  all: "Desde o início",
  custom: "Personalizado",
};

const getLocalDateKey = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateOnlyLocal = (dateValue) => new Date(`${dateValue}T00:00:00`);
const formatDateInput = (date) => getLocalDateKey(date);
const formatCurrencyBRL = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value) || 0);

const getPeriodRange = (preset, customRange) => {
  const now = new Date();
  const today = formatDateInput(now);

  if (preset === "today") {
    return { start_date: today, end_date: today };
  }

  if (preset === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start_date: formatDateInput(start), end_date: today };
  }

  if (preset === "year") {
    const start = new Date(now.getFullYear(), 0, 1);
    return { start_date: formatDateInput(start), end_date: today };
  }

  if (preset === "custom") {
    return {
      start_date: customRange.start_date || "",
      end_date: customRange.end_date || "",
    };
  }

  return { start_date: "", end_date: "" };
};

const formatDateBR = (dateValue) =>
  parseDateOnlyLocal(dateValue).toLocaleDateString("pt-BR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const formatGroupLabel = (dateValue) => {
  const date = parseDateOnlyLocal(dateValue);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const normalize = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const current = normalize(date);

  if (current === normalize(today)) return "Hoje";
  if (current === normalize(yesterday)) return "Ontem";
  return formatDateBR(dateValue);
};

const formatDateFullBR = (dateValue) =>
  new Date(dateValue).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const formatHourBR = (dateValue) =>
  new Date(dateValue).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

const getTransactionMethodType = (method) => {
  const normalized = String(method || "").toLowerCase();
  if (normalized.includes("pix")) return "pix";
  if (normalized.includes("card") || normalized.includes("cart")) return "card";
  return "other";
};

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

const formatPhoneBR = (value) => {
  const digits = onlyDigits(value);
  if (!digits) return "-";
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return value || "-";
};

const getWhatsAppUrl = (value) => {
  return buildAdminWhatsAppUrl(value);
};

const Transactions = () => {
  const PAGE_SIZE = 10;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [periodPreset, setPeriodPreset] = useState("month");
  const [customRange, setCustomRange] = useState({
    start_date: "",
    end_date: "",
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [mobileSearch, setMobileSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorType, setAnchorType] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const observerRef = useRef(null);

  const fetchTransactions = useCallback(async () => {
    const range = getPeriodRange(periodPreset, customRange);
    if (
      periodPreset === "custom" &&
      range.start_date &&
      range.end_date &&
      range.start_date > range.end_date
    ) {
      alert("A data inicial não pode ser maior que a data final.");
      return;
    }

    setLoading(true);
    try {
      const data = await getTransactionsReport({
        ...range,
        type: typeFilter,
        source: sourceFilter,
      });
      const entries = (data.entries || []).map((entry) => ({
        description: "Pagamento",
        amount: entry.amount,
        net_amount: entry.net_amount,
        method: entry.method,
        created_at: entry.created_at,
        transaction_id: entry.transaction_id,
        installment_id: entry.installment_id,
        installment_count: entry.installment_count,
        installment_amount: entry.installment_amount,
        payment_ids: entry.payment_ids,
        user_id: entry.user_id,
        status: entry.status,
        customer_name: entry.customer_name,
        customer_email: entry.customer_email,
        customer_phone: entry.customer_phone,
        customer_is_paid: entry.customer_is_paid,
        customer_subscription_expires_at: entry.customer_subscription_expires_at,
        customer_discount_type: entry.customer_discount_type,
        source: entry.source,
        type: "entry",
      }));

      const expenses = (data.expenses || []).map((expense) => ({
        description: expense.description,
        amount: expense.amount,
        method: "-",
        created_at: expense.created_at,
        status: "paid",
        source: "MANUAL",
        customer_phone: null,
        customer_is_paid: null,
        customer_subscription_expires_at: null,
        customer_discount_type: null,
        type: "expense",
      }));

      const merged = [...entries, ...expenses].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      setTransactions(merged);
    } catch (error) {
      console.error("Erro ao buscar transações:", error);
      alert("Erro ao carregar extrato.");
    } finally {
      setLoading(false);
    }
  }, [periodPreset, customRange, typeFilter, sourceFilter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const summary = useMemo(() => {
    const entradas = transactions
      .filter((item) => item.type === "entry")
      .reduce((acc, item) => acc + Number(item.amount || 0), 0);
    const saidas = transactions
      .filter((item) => item.type === "expense")
      .reduce((acc, item) => acc + Number(item.amount || 0), 0);

    return {
      entradas,
      saidas,
    };
  }, [transactions]);

  const filteredMobileTransactions = useMemo(() => {
    if (!mobileSearch.trim()) return transactions;
    const q = mobileSearch.toLowerCase();
    return transactions.filter((item) => {
      const name = (item.customer_name || "").toLowerCase();
      const desc = (item.description || "").toLowerCase();
      const method = (item.method || "").toLowerCase();
      return name.includes(q) || desc.includes(q) || method.includes(q);
    });
  }, [transactions, mobileSearch]);

  const transactionsForPagination = useMemo(
    () => (isMobile ? filteredMobileTransactions : transactions),
    [isMobile, filteredMobileTransactions, transactions]
  );

  const hasMore = visibleCount < transactionsForPagination.length;

  const visibleTransactions = useMemo(
    () => transactionsForPagination.slice(0, visibleCount),
    [transactionsForPagination, visibleCount]
  );

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [periodPreset, customRange.start_date, customRange.end_date, typeFilter, sourceFilter, mobileSearch]);

  useEffect(() => {
    if (visibleCount > transactionsForPagination.length) {
      setVisibleCount(transactionsForPagination.length || PAGE_SIZE);
    }
  }, [transactionsForPagination.length, visibleCount]);

  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + PAGE_SIZE, transactionsForPagination.length)
          );
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, transactionsForPagination.length]);

  const groupedByDate = useMemo(() => {
    const groups = {};
    visibleTransactions.forEach((item) => {
      const key = getLocalDateKey(item.created_at);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });

    return Object.entries(groups)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([date, items]) => ({ date, items }));
  }, [visibleTransactions]);

  const openMenu = (event, type) => {
    setAnchorEl(event.currentTarget);
    setAnchorType(type);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setAnchorType("");
  };

  return (
    <Box sx={{ p: { xs: 0.35, sm: 2 } }}>
      {!isMobile && (
        <Typography variant="h4" gutterBottom>
          Extrato Financeiro
        </Typography>
      )}
      {isMobile ? (
        <>
          <Box sx={{ px: 0.15, pt: 0.2, pb: 0.8 }}>
            <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1 }}>
              Transações
            </Typography>
            <Typography sx={{ color: "text.secondary", mt: 0.4 }}>
              Extrato
            </Typography>
          </Box>

          <Paper
            sx={{
              p: 1.2,
              borderRadius: 3,
              mb: 1.2,
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: 999,
                px: 1.2,
                py: 0.7,
                backgroundColor: "rgba(255,255,255,0.04)",
                mb: 1,
              }}
            >
              <SearchIcon sx={{ opacity: 0.75 }} />
              <InputBase
                placeholder="Pesquisar"
                value={mobileSearch}
                onChange={(e) => setMobileSearch(e.target.value)}
                sx={{ ml: 1, flex: 1 }}
              />
            </Box>

            <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5 }}>
              <Chip
                label={`Período: ${PERIOD_PRESETS[periodPreset]}`}
                onClick={(e) => openMenu(e, "period")}
                deleteIcon={<KeyboardArrowDownIcon />}
                onDelete={(e) => openMenu(e, "period")}
                sx={{ borderRadius: 999 }}
              />
              <Chip
                label={`Tipo: ${typeFilter ? (typeFilter === "entry" ? "Entradas" : "Saídas") : "Todos"}`}
                onClick={(e) => openMenu(e, "type")}
                deleteIcon={<KeyboardArrowDownIcon />}
                onDelete={(e) => openMenu(e, "type")}
                sx={{ borderRadius: 999 }}
              />
              <Chip
                label={`Origem: ${
                  sourceFilter ? (sourceFilter === "asaas" ? "ASAAS" : "Manual") : "Todas"
                }`}
                onClick={(e) => openMenu(e, "source")}
                deleteIcon={<KeyboardArrowDownIcon />}
                onDelete={(e) => openMenu(e, "source")}
                sx={{ borderRadius: 999 }}
              />
              <Chip label="Aplicar" color="primary" onClick={fetchTransactions} />
            </Stack>
          </Paper>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
            {anchorType === "period" &&
              Object.entries(PERIOD_PRESETS).map(([value, label]) => (
                <MenuItem
                  key={value}
                  onClick={() => {
                    setPeriodPreset(value);
                    closeMenu();
                  }}
                >
                  {label}
                </MenuItem>
              ))}
            {anchorType === "type" && (
              <>
                <MenuItem onClick={() => { setTypeFilter(""); closeMenu(); }}>Todos</MenuItem>
                <MenuItem onClick={() => { setTypeFilter("entry"); closeMenu(); }}>Entradas</MenuItem>
                <MenuItem onClick={() => { setTypeFilter("expense"); closeMenu(); }}>Saídas</MenuItem>
              </>
            )}
            {anchorType === "source" && (
              <>
                <MenuItem onClick={() => { setSourceFilter(""); closeMenu(); }}>Todas</MenuItem>
                <MenuItem onClick={() => { setSourceFilter("asaas"); closeMenu(); }}>ASAAS</MenuItem>
                <MenuItem onClick={() => { setSourceFilter("manual"); closeMenu(); }}>Manual</MenuItem>
              </>
            )}
          </Menu>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1,
              mb: 1.4,
            }}
          >
            <Paper sx={{ p: 1.1 }}>
              <Typography variant="caption">Entradas</Typography>
              <Typography sx={{ fontWeight: 700, color: "success.main", fontSize: 14 }}>
                {formatCurrencyBRL(summary.entradas)}
              </Typography>
            </Paper>
            <Paper sx={{ p: 1.1 }}>
              <Typography variant="caption">Saídas</Typography>
              <Typography sx={{ fontWeight: 700, color: "error.main", fontSize: 14 }}>
                {formatCurrencyBRL(summary.saidas)}
              </Typography>
            </Paper>
          </Box>

          <Stack spacing={0.8}>
            {!loading && transactionsForPagination.length === 0 && (
              <Paper sx={{ p: 2, textAlign: "center" }}>Nenhuma transação encontrada.</Paper>
            )}
            {groupedByDate.map((group) => (
              <Paper key={group.date} sx={{ p: 1.3, borderRadius: 2 }}>
                {(() => {
                  const receivedInDay = group.items
                    .filter((item) => item.type === "entry")
                    .reduce((acc, item) => acc + Number(item.amount || 0), 0);

                  return (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    mb: 1.2,
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>{formatGroupLabel(group.date)}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    Recebido no dia: {formatCurrencyBRL(receivedInDay)}
                  </Typography>
                </Box>
                  );
                })()}
                <Divider sx={{ mb: 1 }} />
                {group.items.map((transaction, index) => (
                  <Box
                    key={`${transaction.transaction_id || "manual"}-${index}`}
                    onClick={() => setSelectedTransaction(transaction)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 1.2,
                      borderBottom:
                        index !== group.items.length - 1
                          ? "1px solid rgba(255,255,255,0.08)"
                          : "none",
                      cursor: "pointer",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.1 }}>
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          bgcolor: "rgba(255,255,255,0.07)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {(() => {
                          const methodType = getTransactionMethodType(transaction.method);
                          if (methodType === "pix") return <PixIcon fontSize="small" />;
                          if (methodType === "card") return <CreditCardIcon fontSize="small" />;
                          return <ReceiptLongIcon fontSize="small" />;
                        })()}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700 }}>
                          {transaction.type === "entry" ? "Recebido" : "Despesa"}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.75 }}>
                          {formatPersonName(transaction.customer_name) || transaction.description || "-"}
                        </Typography>
                        {Number(transaction.installment_count || 1) > 1 && (
                          <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>
                            {transaction.installment_count} parcelas de{" "}
                            {formatCurrencyBRL(transaction.installment_amount)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color: transaction.type === "entry" ? "success.main" : "error.main",
                        }}
                      >
                        {transaction.type === "entry" ? "" : "- "}
                        {formatCurrencyBRL(transaction.amount)}
                      </Typography>
                      {transaction.type === "entry" && transaction.net_amount != null && (
                        <Typography variant="caption" sx={{ display: "block", color: "#5DA9FF", mt: 0.2 }}>
                          Líquido {formatCurrencyBRL(transaction.net_amount)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Paper>
            ))}
          </Stack>

          {hasMore && (
            <Box
              ref={observerRef}
              sx={{ display: "flex", justifyContent: "center", py: 2, minHeight: 72 }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          <Dialog fullScreen open={Boolean(selectedTransaction)} onClose={() => setSelectedTransaction(null)}>
            {selectedTransaction && (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <IconButton onClick={() => setSelectedTransaction(null)}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography sx={{ fontWeight: 700 }}>
                    {selectedTransaction.type === "entry" ? "Recebimento" : "Despesa"}
                  </Typography>
                  <IconButton>
                    <HelpOutlineIcon />
                  </IconButton>
                </Box>

                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Box
                    sx={{
                      width: 76,
                      height: 76,
                      borderRadius: "50%",
                      margin: "0 auto 16px",
                      bgcolor: "rgba(255,255,255,0.06)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {(() => {
                      const methodType = getTransactionMethodType(selectedTransaction.method);
                      if (methodType === "pix") return <PixIcon sx={{ fontSize: 42 }} />;
                      if (methodType === "card") return <CreditCardIcon sx={{ fontSize: 42 }} />;
                      return <ReceiptLongIcon sx={{ fontSize: 42 }} />;
                    })()}
                  </Box>
                  <Typography sx={{ fontSize: 46, fontWeight: 800 }}>
                    {formatCurrencyBRL(selectedTransaction.amount)}
                  </Typography>
                  <Typography sx={{ mt: 0.6, opacity: 0.75 }}>
                    {formatPersonName(selectedTransaction.customer_name) || "Cliente não identificado"}
                  </Typography>
                </Box>

                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography sx={{ fontWeight: 800, mb: 1.4 }}>Sobre a transação</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 1.4 }}>
                    <Typography sx={{ opacity: 0.8 }}>Data da transação</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {formatDateFullBR(selectedTransaction.created_at)}
                    </Typography>
                    <Typography sx={{ opacity: 0.8 }}>Horário</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatHourBR(selectedTransaction.created_at)}</Typography>
                    <Typography sx={{ opacity: 0.8 }}>ID da transação</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {selectedTransaction.transaction_id || "-"}
                      </Typography>
                      {selectedTransaction.transaction_id && (
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard.writeText(selectedTransaction.transaction_id)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    {Number(selectedTransaction.installment_count || 1) > 1 && (
                      <>
                        <Typography sx={{ opacity: 0.8 }}>Parcelamento</Typography>
                        <Typography sx={{ fontWeight: 700 }}>
                          {selectedTransaction.installment_count}x de{" "}
                          {formatCurrencyBRL(selectedTransaction.installment_amount)}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Paper>

                <Paper sx={{ p: 2 }}>
                  <Typography sx={{ fontWeight: 800, mb: 1.4 }}>Quem enviou</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 1.4 }}>
                    <Typography sx={{ opacity: 0.8 }}>Nome</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {formatPersonName(selectedTransaction.customer_name) || "-"}
                    </Typography>
                    <Typography sx={{ opacity: 0.8 }}>E-mail</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {selectedTransaction.customer_email || "-"}
                    </Typography>
                    <Typography sx={{ opacity: 0.8 }}>Telefone</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {formatPhoneBR(selectedTransaction.customer_phone)}
                    </Typography>
                    <Typography sx={{ opacity: 0.8 }}>Instituição</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {selectedTransaction.source === "ASAAS" ? "ASAAS" : "Manual"}
                    </Typography>
                    <Typography sx={{ opacity: 0.8 }}>Forma</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{selectedTransaction.method || "-"}</Typography>
                    <Typography sx={{ opacity: 0.8 }}>Status</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{selectedTransaction.status || "-"}</Typography>
                    <Typography sx={{ opacity: 0.8 }}>Valor líquido</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {selectedTransaction.net_amount != null
                        ? formatCurrencyBRL(selectedTransaction.net_amount)
                        : "-"}
                    </Typography>
                    <Typography sx={{ opacity: 0.8 }}>Status da assinatura</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {selectedTransaction.customer_is_paid == null
                        ? "-"
                        : selectedTransaction.customer_is_paid === 1
                        ? "Pago"
                        : "Não pago"}
                    </Typography>
                    <Typography sx={{ opacity: 0.8 }}>Vencimento</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {selectedTransaction.customer_subscription_expires_at
                        ? new Date(selectedTransaction.customer_subscription_expires_at).toLocaleDateString(
                            "pt-BR"
                          )
                        : "-"}
                    </Typography>
                    <Typography sx={{ opacity: 0.8 }}>Desconto</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {selectedTransaction.customer_discount_type || "-"}
                    </Typography>
                    <Typography sx={{ opacity: 0.8 }}>Usuário</Typography>
                    <Typography sx={{ fontWeight: 700 }}>
                      {selectedTransaction.user_id || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<WhatsAppIcon />}
                      disabled={!getWhatsAppUrl(selectedTransaction.customer_phone)}
                      onClick={() => {
                        openAdminWhatsApp(selectedTransaction.customer_phone);
                      }}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!selectedTransaction.customer_email}
                      onClick={() => {
                        if (selectedTransaction.customer_email) {
                          navigator.clipboard.writeText(selectedTransaction.customer_email);
                        }
                      }}
                    >
                      Copiar e-mail
                    </Button>
                  </Box>
                </Paper>
              </Box>
            )}
          </Dialog>
        </>
      ) : (
        <>
          <Typography variant="h4" gutterBottom>
        Extrato Financeiro
      </Typography>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Período da fatura</Typography>
        <ToggleButtonGroup
          value={periodPreset}
          exclusive
          onChange={(_, value) => {
            if (value) setPeriodPreset(value);
          }}
          sx={{
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
            width: "100%",
            justifyContent: isMobile ? "flex-start" : "center",
          }}
        >
          {Object.entries(PERIOD_PRESETS).map(([key, label]) => (
            <ToggleButton
              key={key}
              value={key}
              sx={{ textTransform: "none", px: isMobile ? 1.2 : 2 }}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          {periodPreset === "custom" && (
            <>
              <TextField
                label="Data Inicial"
                type="date"
                name="start_date"
                value={customRange.start_date}
                onChange={(e) =>
                  setCustomRange((prev) => ({ ...prev, start_date: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ flex: 1, minWidth: isMobile ? "100%" : 220 }}
              />
              <TextField
                label="Data Final"
                type="date"
                name="end_date"
                value={customRange.end_date}
                onChange={(e) =>
                  setCustomRange((prev) => ({ ...prev, end_date: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ flex: 1, minWidth: isMobile ? "100%" : 220 }}
              />
            </>
          )}

          <TextField
            label="Tipo"
            name="type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            select
            fullWidth
            sx={{ flex: 1, minWidth: isMobile ? "100%" : 180 }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="entry">Entradas</MenuItem>
            <MenuItem value="expense">Saídas</MenuItem>
          </TextField>
          <TextField
            label="Origem"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            select
            fullWidth
            sx={{ flex: 1, minWidth: isMobile ? "100%" : 180 }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="asaas">ASAAS</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
          </TextField>

          <Button
            variant="contained"
            onClick={fetchTransactions}
            sx={{
              alignSelf: "center",
              height: 50,
              width: isMobile ? "100%" : "auto",
              px: 3,
            }}
            disabled={loading}
          >
            Aplicar filtros
          </Button>
        </Box>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="body2">Entradas</Typography>
          <Typography sx={{ fontWeight: 700, color: "success.main" }}>
            {formatCurrencyBRL(summary.entradas)}
          </Typography>
        </Paper>
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="body2">Saídas</Typography>
          <Typography sx={{ fontWeight: 700, color: "error.main" }}>
            {formatCurrencyBRL(summary.saidas)}
          </Typography>
        </Paper>
      </Box>

      {isMobile ? (
        <Stack spacing={1.2}>
          {!loading && transactionsForPagination.length === 0 && (
            <Paper sx={{ p: 2, textAlign: "center" }}>Nenhuma transação encontrada.</Paper>
          )}
          {visibleTransactions.map((transaction, index) => (
            <Paper key={index} sx={{ p: 1.5, borderRadius: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>
                    {formatPersonName(transaction.customer_name) || "Cliente não identificado"}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {new Date(transaction.created_at).toLocaleDateString("pt-BR")}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: transaction.type === "entry" ? "success.main" : "error.main",
                  }}
                >
                  {transaction.type === "entry"
                    ? `+ ${formatCurrencyBRL(transaction.amount)}`
                    : `- ${formatCurrencyBRL(transaction.amount)}`}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                <Chip size="small" label={transaction.source || "MANUAL"} />
                <Chip size="small" label={transaction.method || "Pagamento"} />
                <Chip size="small" label={transaction.status || "-"} />
                {Number(transaction.installment_count || 1) > 1 && (
                  <Chip
                    size="small"
                    label={`${transaction.installment_count}x de ${formatCurrencyBRL(
                      transaction.installment_amount
                    )}`}
                  />
                )}
              </Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Paper sx={{ overflow: "hidden", borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Lançamento</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell>ID Transação</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && transactionsForPagination.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Nenhuma transação encontrada.
                    </TableCell>
                  </TableRow>
                )}
                {visibleTransactions.map((transaction, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {new Date(transaction.created_at).toLocaleDateString("pt-BR")}
                    </TableCell>
                    <TableCell>
                      {formatPersonName(transaction.customer_name) || "-"}
                      {transaction.customer_email ? (
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>
                          {transaction.customer_email}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{transaction.description || "-"}</TableCell>
                    <TableCell>
                      {transaction.type === "entry"
                        ? `${transaction.source || "-"} • ${transaction.method || "Pagamento"}`
                        : "Despesa"}
                      {Number(transaction.installment_count || 1) > 1 && (
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.75 }}>
                          Parcelado: {transaction.installment_count}x de{" "}
                          {formatCurrencyBRL(transaction.installment_amount)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {transaction.transaction_id || "-"}
                      {Number(transaction.installment_count || 1) > 1 && transaction.payment_ids && (
                        <Typography variant="caption" sx={{ display: "block", opacity: 0.75 }}>
                          {transaction.payment_ids.split(",").length} cobranças
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{transaction.status || "-"}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: transaction.type === "entry" ? "success.main" : "error.main",
                        fontWeight: 700,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "inherit",
                          fontWeight: 700,
                          lineHeight: 1.2,
                        }}
                      >
                        {transaction.type === "entry"
                          ? `+ ${formatCurrencyBRL(transaction.amount)}`
                          : `- ${formatCurrencyBRL(transaction.amount)}`}
                      </Typography>
                      {transaction.type === "entry" && transaction.net_amount != null && (
                        <Typography variant="caption" sx={{ color: "#5DA9FF", lineHeight: 1.1 }}>
                          Líquido {formatCurrencyBRL(transaction.net_amount)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {hasMore && (
        <Box
          ref={observerRef}
          sx={{ display: "flex", justifyContent: "center", mt: 1.5, minHeight: 84 }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
        </>
      )}
    </Box>
  );
};

export default Transactions;
